<template>
  <div id="measurements" class="measurements-wrap">
    <div class="toolbar">
      <div class="device-picker" ref="picker">
        <button
          type="button"
          class="ddl-trigger"
          :class="{ 'ddl-open': dropdownOpen, 'has-value': !!selectedDevice }"
          @click="toggleDropdown"
          aria-haspopup="listbox"
          :aria-expanded="dropdownOpen ? 'true' : 'false'"
        >
          <span class="ddl-value">
            {{ selectedDevice || "Select device…" }}
          </span>
          <span
            v-if="selectedDevice"
            class="ddl-clear"
            role="button"
            tabindex="-1"
            aria-label="Clear device"
            @click.stop="clearDevice"
            @mousedown.prevent
          >
            <i class="fa fa-times" />
          </span>
          <i class="fa fa-chevron-down ddl-chevron" aria-hidden="true" />
        </button>
        <div v-if="dropdownOpen" class="ddl-panel" role="listbox">
          <div class="ddl-search">
            <i class="fa fa-search ddl-search-icon" aria-hidden="true" />
            <input
              ref="ddlSearch"
              v-model="deviceFilter"
              type="text"
              placeholder="Type MAC to filter…"
              class="ddl-search-input"
              @keydown.esc.prevent="closeDropdown"
              @keydown.enter.prevent="selectFirst"
            />
          </div>
          <div class="ddl-list">
            <div
              v-for="d in filteredDevices"
              :key="d"
              class="ddl-item"
              :class="{ selected: d === selectedDevice }"
              role="option"
              :aria-selected="d === selectedDevice ? 'true' : 'false'"
              @click="selectDevice(d)"
            >
              {{ d }}
            </div>
            <div v-if="filteredDevices.length === 0" class="ddl-empty">
              No matches
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="selectedDevice && !isLoading && tests.length"
        class="summary-chip"
      >
        <strong>{{ tests.length }}</strong>
        <span class="summary-label">test{{ tests.length === 1 ? "" : "s" }}</span>
        <span class="summary-dot">·</span>
        <strong>{{ totalRows.toLocaleString() }}</strong>
        <span class="summary-label">rows</span>
      </div>
      <div class="toolbar-spacer" />
      <b-button
        v-if="selectedDevice && !isLoading && tests.length"
        size="sm"
        class="export-btn"
        @click="openRangeExport"
      >
        <i class="fa fa-file-export" /> Export
      </b-button>
    </div>

    <div v-if="listError" class="adm-error-banner" role="alert">
      <i class="fa fa-circle-exclamation" aria-hidden="true" />
      <span>{{ listError }}</span>
    </div>

    <!-- Range export modal -->
    <b-modal
      v-model="rangeOpen"
      header-class="rng-modal-header"
      body-class="rng-modal-body"
      footer-class="rng-modal-footer"
      modal-class="rng-modal"
      :busy="rangeExporting"
      no-close-on-backdrop
      @hidden="onRangeHidden"
    >
      <template #modal-header>
        <h5 class="rng-title">Export Measurements</h5>
        <button
          type="button"
          class="rng-close-btn"
          aria-label="Close"
          @click="rangeOpen = false"
        >
          <i class="fa fa-times" />
        </button>
      </template>
      <p class="rng-lead">
        Select a date range to export all measurements for
        <strong>{{ selectedDevice }}</strong>.
      </p>
      <div class="rng-fields">
        <div class="rng-field">
          <label for="rng-from" class="rng-label">From</label>
          <b-calendar
            id="rng-from"
            v-model="rangeFrom"
            :min="rangeMinDay"
            :max="rangeTo || rangeMaxDay"
            hide-header
            weekday-header-format="narrow"
            start-weekday="1"
            label-help=""
            block
            class="rng-calendar"
          />
        </div>
        <div class="rng-field">
          <label for="rng-to" class="rng-label">To</label>
          <b-calendar
            id="rng-to"
            v-model="rangeTo"
            :min="rangeFrom || rangeMinDay"
            :max="rangeMaxDay"
            hide-header
            weekday-header-format="narrow"
            start-weekday="1"
            label-help=""
            block
            class="rng-calendar"
          />
        </div>
      </div>
      <p v-if="!rangeValid && rangeFrom && rangeTo" class="rng-err">
        "From" must not be after "To".
      </p>
      <p v-if="rangeError" class="rng-err">{{ rangeError }}</p>
      <p v-if="rangeExporting" class="rng-hint">
        <i class="fa fa-spinner fa-spin" /> Fetching rows…
      </p>

      <template #modal-footer="{ cancel }">
        <button
          type="button"
          class="rng-btn-cancel"
          :disabled="rangeExporting"
          @click="cancel"
        >
          Cancel
        </button>
        <button
          type="button"
          class="rng-btn-ok"
          :disabled="rangeExporting || !rangeValid"
          @click="performRangeExport"
        >
          <i v-if="rangeExporting" class="fa fa-spinner fa-spin" />
          <i v-else class="fa fa-file-export" />
          Export
        </button>
      </template>
    </b-modal>

    <div v-if="isLoading" class="loading-screen">
      <img src="../assets/admin_loading.gif" alt="Loading" class="loader" />
      <p>Loading data...</p>
    </div>

    <div id="adminDetails" v-else-if="tests.length > 0" class="tests-card">
      <b-table
        :items="tests"
        :fields="testFields"
        hover
        small
        responsive
        :per-page="testsPerPage"
        :current-page="testsPage"
        class="tests-table"
      >
        <template #cell(row_count)="row">
          <span class="rows-chip">{{
            Number(row.item.row_count || 0).toLocaleString()
          }}</span>
        </template>
        <template #cell(actions)="row">
          <div class="action-group">
            <button
              type="button"
              class="action-btn"
              title="Visualize"
              aria-label="Visualize"
              @click="openVisualize(row.item)"
            >
              <i class="fa fa-chart-line" />
            </button>
            <button
              type="button"
              class="action-btn"
              :class="{ 'action-btn--busy': exportingKey === exportKey(row.item) }"
              :title="exportingKey === exportKey(row.item) ? 'Exporting…' : 'Export'"
              aria-label="Export"
              :disabled="exportingKey === exportKey(row.item)"
              @click="exportTest(row.item)"
            >
              <i
                :class="
                  exportingKey === exportKey(row.item)
                    ? 'fa fa-spinner fa-spin'
                    : 'fa fa-file-csv'
                "
              />
            </button>
          </div>
        </template>
      </b-table>
      <div v-if="tests.length > testsPerPage" class="tests-pagination">
        <b-pagination
          v-model="testsPage"
          :total-rows="tests.length"
          :per-page="testsPerPage"
          first-number
          last-number
          aria-controls="tests-table"
        />
      </div>
    </div>

    <div class="empty-state" v-else>
      <div class="empty-icon">
        <i
          :class="selectedDevice ? 'fa fa-folder-open' : 'fa fa-microchip'"
        />
      </div>
      <div class="empty-title">
        {{
          selectedDevice
            ? "No measurements for this device"
            : "Select a device to begin"
        }}
      </div>
      <div class="empty-sub">
        {{
          selectedDevice
            ? "Once data lands for this MAC, the test sessions will appear here."
            : "Use the search box above and pick a device with reported tests."
        }}
      </div>
    </div>

    <b-modal
      v-model="vizOpen"
      size="xl"
      scrollable
      hide-footer
      header-class="viz-modal-header"
      body-class="viz-modal-body"
      modal-class="viz-modal"
      @shown="onVizShown"
      @hidden="onVizClosed"
    >
      <template #modal-header>
        <div class="viz-title-block">
          <h5 class="viz-title">Visualize test</h5>
          <span v-if="vizTest && vizTest.test_started_at" class="viz-subtitle">
            {{ fmtMs(vizTest.test_started_at) }}
          </span>
        </div>
        <button
          type="button"
          class="viz-close-btn"
          aria-label="Close"
          @click="vizOpen = false"
        >
          <i class="fa fa-times" />
        </button>
      </template>

      <div v-if="vizLoading" class="viz-loading">
        <img src="../assets/admin_loading.gif" alt="Loading" class="loader" />
        <p>Loading measurements…</p>
      </div>
      <div v-else-if="vizError" class="viz-error">
        <i class="fa fa-circle-exclamation" /> {{ vizError }}
      </div>
      <div v-else-if="!vizRows.length" class="viz-loading">No data.</div>
      <div v-else>
        <div class="viz-meta">
          <div class="viz-meta-left">
            <span class="adm-chip">
              <strong>{{ vizRows.length.toLocaleString() }}</strong>
              <span class="adm-chip-label">points</span>
            </span>
            <span v-if="!vizFocusKey" class="viz-meta-hint">
              <i class="fa fa-circle-info" /> Click a panel to zoom
            </span>
          </div>
          <button
            v-if="vizFocusKey"
            type="button"
            class="adm-btn-ghost viz-back-btn"
            @click="vizFocusKey = null"
          >
            <i class="fa fa-arrow-left" /> Back to all
          </button>
        </div>

        <div v-if="!vizFocusKey && xMax > xMin" class="range-slider">
          <div class="range-row">
            <div class="range-track">
              <div
                class="range-track-fill"
                :style="{
                  left: rangePct(xStart) + '%',
                  right: 100 - rangePct(xEnd) + '%',
                }"
              />
              <input
                type="range"
                :min="xMin"
                :max="xMax"
                :step="rangeStep"
                :value="xStart"
                @input="onStartInput($event)"
              />
              <input
                type="range"
                :min="xMin"
                :max="xMax"
                :step="rangeStep"
                :value="xEnd"
                @input="onEndInput($event)"
              />
            </div>
          </div>
          <div class="range-labels">
            <span>{{ fmtElapsed(xStart) }}</span>
            <span>{{ fmtElapsed(xEnd) }}</span>
          </div>
        </div>

        <div class="viz-grid" :class="{ 'has-focus': vizFocusKey }">
          <div
            v-for="p in panels"
            :key="p.key"
            class="viz-panel"
            :class="{ 'viz-panel--focused': vizFocusKey === p.key }"
            @click="toggleFocus(p.key)"
          >
            <div class="viz-panel-header">
              <h6 class="viz-panel-title">{{ p.label }}</h6>
              <i
                v-if="vizFocusKey === p.key"
                class="fa fa-compress viz-panel-icon"
                aria-hidden="true"
              />
              <i
                v-else
                class="fa fa-expand viz-panel-icon"
                aria-hidden="true"
              />
            </div>
            <div v-if="panelEmpty[p.key]" class="viz-panel-empty">No data</div>
            <canvas
              v-else
              :ref="`canvas-${p.key}`"
              :key="`canvas-${p.key}-${vizSession}`"
            />
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { customAxios } from "@/utils/customAxios";
import { apiErrorMessage } from "@/utils/apiError";
import { Chart, registerables } from "chart.js";

Chart.register(...registerables);

const PRIMARY = "#2a345d";
const SENSOR_COLORS = [PRIMARY, "#d9534f", "#5cb85c", "#f0ad4e"];

const PANELS = [
  {
    key: "temperature",
    label: "Temperature",
    series: [{ key: "temperature", label: "Temperature", color: PRIMARY }],
  },
  {
    key: "humidity",
    label: "Humidity",
    series: [{ key: "humidity", label: "Humidity", color: PRIMARY }],
  },
  {
    key: "sensors",
    label: "Sensors 1–4",
    series: [1, 2, 3, 4].map((i) => ({
      key: `sensor${i}`,
      label: `Sensor ${i}`,
      color: SENSOR_COLORS[i - 1],
    })),
  },
  {
    key: "heater_voltage",
    label: "Heater Voltage",
    series: [{ key: "heater_voltage", label: "Heater Voltage", color: PRIMARY }],
  },
];

const pad = (n, w = 2) => String(n).padStart(w, "0");

// Parse a backend timestamp into ms. Treats values lacking a timezone marker
// as UTC, matching how Laravel's `datetime` cast serializes (`...Z`).
function parseTime(v) {
  if (!v) return null;
  const s = String(v);
  const hasTz = /(Z|[+-]\d{2}:?\d{2})$/i.test(s);
  const t = Date.parse(hasTz ? s : `${s.replace(" ", "T")}Z`);
  return Number.isFinite(t) ? t : null;
}

function fmtMs(v) {
  const t = parseTime(v);
  if (t === null) return "";
  const d = new Date(t);
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.` +
    `${pad(d.getMilliseconds(), 3)}`
  );
}

// Convert a JS epoch-ms to a "YYYY-MM-DD" string in the *local* timezone —
// the format <input type="date"> expects.
function toLocalDateInputValue(ms) {
  if (ms === null || ms === undefined) return "";
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function fmtElapsed(ms) {
  if (ms === null || ms === undefined || isNaN(ms)) return "";
  const sign = ms < 0 ? "-" : "";
  const a = Math.abs(ms);
  const h = Math.floor(a / 3600000);
  const m = Math.floor((a % 3600000) / 60000);
  const s = Math.floor((a % 60000) / 1000);
  const f = Math.floor(a % 1000);
  return h > 0
    ? `${sign}${h}:${pad(m)}:${pad(s)}.${pad(f, 3)}`
    : `${sign}${m}:${pad(s)}.${pad(f, 3)}`;
}

export default {
  data() {
    return {
      isLoading: false,
      devices: [],
      tests: [],
      selectedDevice: "",
      testFields: [
        {
          key: "test_started_at",
          label: "Started At",
          sortable: true,
          formatter: (v) => (v ? fmtMs(v) : "Unknown start"),
          tdClass: "ts-cell",
        },
        {
          key: "row_count",
          label: "Rows",
          sortable: true,
          tdClass: "rows-col",
          thClass: "rows-col",
        },
        {
          key: "config",
          label: "Intervals (s)",
          formatter: (v) => v || "—",
          tdClass: "config-col",
          thClass: "config-col",
        },
        { key: "actions", label: "", tdClass: "actions-col", thClass: "actions-col" },
      ],
      panels: PANELS,
      panelEmpty: {},
      listError: "",
      vizOpen: false,
      vizLoading: false,
      vizError: "",
      vizRows: [],
      vizTest: null,
      vizFocusKey: null,
      charts: {},
      xMin: 0,
      xMax: 0,
      xStart: 0,
      xEnd: 0,
      // Monotonic ids — only the latest request applies its results.
      testsReqId: 0,
      vizReqId: 0,
      // Bumped on every modal open; used as a :key so Vue recreates the
      // canvas DOM elements per session — defends against any chart state
      // leaking across an open/close/open cycle.
      vizSession: 0,
      // Set true once b-modal's @shown event fires — charts are only
      // attached when this is true *and* data has arrived, otherwise the
      // canvases are still 0×0 from the in-flight transition.
      vizShown: false,
      exportingKey: "",
      rangeOpen: false,
      rangeExporting: false,
      rangeFrom: "",
      rangeTo: "",
      rangeError: "",
      // searchable device dropdown
      dropdownOpen: false,
      deviceFilter: "",
      // tests pagination
      testsPerPage: 20,
      testsPage: 1,
    };
  },
  computed: {
    totalRows() {
      return this.tests.reduce((sum, t) => sum + (Number(t.row_count) || 0), 0);
    },
    filteredDevices() {
      const q = (this.deviceFilter || "").trim().toLowerCase();
      if (!q) return this.devices;
      return this.devices.filter((d) => d.toLowerCase().includes(q));
    },
    vizTitle() {
      if (!this.vizTest) return "Visualize";
      const ts = this.vizTest.test_started_at
        ? fmtMs(this.vizTest.test_started_at)
        : "Unknown start";
      return `Visualize · ${ts}`;
    },
    rangeStep() {
      return 1;
    },
    // Earliest/latest available data, derived from the tests list.
    rangeBounds() {
      let min = null;
      let max = null;
      for (const t of this.tests) {
        const tStart = parseTime(t.test_started_at);
        if (tStart === null) continue;
        if (min === null || tStart < min) min = tStart;
        // Approximate test end as start + (row_count - 1)*1s. Coarse, but
        // good enough for setting a default upper bound; the user can refine.
        const approxEnd = tStart + Math.max(0, (Number(t.row_count) || 1) - 1) * 1000;
        if (max === null || approxEnd > max) max = approxEnd;
      }
      return { min, max };
    },
    rangeValid() {
      if (!this.rangeFrom || !this.rangeTo) return false;
      // Allow same-day exports; reject when From comes after To.
      return this.rangeFrom <= this.rangeTo;
    },
    rangeMinDay() {
      const { min } = this.rangeBounds;
      return min !== null ? toLocalDateInputValue(min) : "";
    },
    rangeMaxDay() {
      const { max } = this.rangeBounds;
      return max !== null ? toLocalDateInputValue(max) : "";
    },
  },
  watch: {
    selectedDevice(val) {
      // Switching devices: close any open viz and drop in-flight state.
      this.closeViz();
      this.testsPage = 1;
      if (val && this.devices.includes(val)) {
        this.fetchTests();
      } else {
        this.tests = [];
      }
    },
  },
  mounted() {
    this.fetchDevices();
  },
  beforeDestroy() {
    document.removeEventListener("mousedown", this.handleOutsideClick);
  },
  methods: {
    fmtElapsed,
    fmtMs,
    toggleDropdown() {
      this.dropdownOpen ? this.closeDropdown() : this.openDropdown();
    },
    openDropdown() {
      this.dropdownOpen = true;
      this.deviceFilter = "";
      this.$nextTick(() => {
        if (this.$refs.ddlSearch) this.$refs.ddlSearch.focus();
      });
      document.addEventListener("mousedown", this.handleOutsideClick);
    },
    closeDropdown() {
      this.dropdownOpen = false;
      document.removeEventListener("mousedown", this.handleOutsideClick);
    },
    handleOutsideClick(e) {
      const root = this.$refs.picker;
      if (!root || !root.contains(e.target)) {
        this.closeDropdown();
      }
    },
    selectDevice(d) {
      this.selectedDevice = d;
      this.closeDropdown();
    },
    clearDevice() {
      this.selectedDevice = "";
      this.closeDropdown();
    },
    selectFirst() {
      if (this.filteredDevices.length > 0) {
        this.selectDevice(this.filteredDevices[0]);
      }
    },
    fetchDevices() {
      customAxios
        .get("/measurements/devices")
        .then((res) => {
          this.devices = Array.isArray(res.data && res.data.data)
            ? res.data.data
            : [];
          this.listError = "";
        })
        .catch((err) => {
          console.error("Failed to load device list", err);
          this.devices = [];
          this.listError = apiErrorMessage(
            err,
            "Failed to load the device list."
          );
        });
    },
    fetchTests() {
      this.isLoading = true;
      const reqId = ++this.testsReqId;
      customAxios
        .get("/measurements/tests", {
          params: { device_id: this.selectedDevice },
        })
        .then((res) => {
          if (reqId !== this.testsReqId) return; // stale
          this.tests = Array.isArray(res.data && res.data.data)
            ? res.data.data
            : [];
          this.listError = "";
        })
        .catch((err) => {
          if (reqId !== this.testsReqId) return;
          console.error("Failed to load tests", err);
          this.tests = [];
          this.listError = apiErrorMessage(
            err,
            "Failed to load tests for this device."
          );
        })
        .finally(() => {
          if (reqId !== this.testsReqId) return;
          this.isLoading = false;
        });
    },
    openVisualize(test) {
      // Wipe everything from any prior session — vizFocusKey in particular,
      // otherwise the new session inherits the old zoom and renders panels
      // inside display:none containers (0x0 canvases → empty charts).
      this.disposeCharts();
      this.vizTest = test;
      this.vizOpen = true;
      this.vizShown = false;
      this.vizLoading = true;
      this.vizError = "";
      this.vizRows = [];
      this.panelEmpty = {};
      this.vizFocusKey = null;
      this.vizSession++;
      const reqId = ++this.vizReqId;
      customAxios
        .get("/measurements", {
          params: {
            device_id: this.selectedDevice,
            test_started_at: test.test_started_at,
          },
        })
        .then((res) => {
          if (reqId !== this.vizReqId) return;
          this.vizRows = Array.isArray(res.data && res.data.data)
            ? res.data.data
            : [];
        })
        .catch((err) => {
          if (reqId !== this.vizReqId) return;
          console.error("Failed to load measurements", err);
          this.vizError = apiErrorMessage(err, "Failed to load measurements.");
        })
        .finally(() => {
          if (reqId !== this.vizReqId) return;
          this.vizLoading = false;
          this.maybeRenderCharts();
        });
    },
    onVizShown() {
      // Modal transition complete — canvases now have real dimensions.
      this.vizShown = true;
      this.maybeRenderCharts();
    },
    maybeRenderCharts() {
      // Both data and "modal fully shown" must be true; otherwise we'd be
      // attaching Chart.js to 0×0 canvases and get blank rectangles.
      if (
        !this.vizShown ||
        this.vizLoading ||
        this.vizError ||
        !this.vizRows.length
      ) {
        return;
      }
      // $nextTick alone isn't enough — Vue updates the DOM but the
      // browser may not have done layout yet. requestAnimationFrame
      // schedules right before the next paint, after layout has run, so
      // canvas offsetWidth/Height reflect their final CSS-driven size.
      this.$nextTick(() => {
        requestAnimationFrame(() => this.renderCharts());
      });
    },
    rangePct(v) {
      const span = this.xMax - this.xMin;
      if (span <= 0) return 0;
      return ((v - this.xMin) / span) * 100;
    },
    onStartInput(e) {
      const v = Number(e.target.value);
      const clamped = Math.min(v, this.xEnd - this.rangeStep);
      if (v !== clamped) e.target.value = String(clamped);
      this.xStart = clamped;
      this.applyRange();
    },
    onEndInput(e) {
      const v = Number(e.target.value);
      const clamped = Math.max(v, this.xStart + this.rangeStep);
      if (v !== clamped) e.target.value = String(clamped);
      this.xEnd = clamped;
      this.applyRange();
    },
    applyRange() {
      for (const k of Object.keys(this.charts)) {
        const ch = this.charts[k];
        if (!ch) continue;
        ch.options.scales.x.min = this.xStart;
        ch.options.scales.x.max = this.xEnd;
        ch.update("none");
      }
    },
    renderCharts() {
      const times = this.vizRows.map((r) => parseTime(r.measured_at));
      const t0 = times.find((t) => t !== null) ?? 0;
      const offsets = times.map((t) => (t === null ? null : t - t0));
      let xMin = Infinity;
      let xMax = -Infinity;
      for (const o of offsets) {
        if (o === null) continue;
        if (o < xMin) xMin = o;
        if (o > xMax) xMax = o;
      }
      if (!Number.isFinite(xMin)) {
        xMin = 0;
        xMax = 0;
      }
      this.xMin = xMin;
      this.xMax = xMax;
      this.xStart = xMin;
      this.xEnd = xMax;
      const panelEmpty = {};
      for (const p of PANELS) {
        const hasData = p.series.some((s) =>
          this.vizRows.some(
            (r) => r[s.key] !== null && r[s.key] !== undefined
          )
        );
        panelEmpty[p.key] = !hasData;
        if (this.charts[p.key]) {
          this.charts[p.key].destroy();
          delete this.charts[p.key];
        }
        if (!hasData) continue;
        const ref = this.$refs[`canvas-${p.key}`];
        const canvas = Array.isArray(ref) ? ref[0] : ref;
        if (!canvas) continue;
        // Defensive: if Chart.js still has a chart attached to this canvas
        // (from a previous session whose tracked reference we lost), kill it
        // before re-binding. Otherwise `new Chart(canvas, ...)` throws
        // "Canvas is already in use".
        const stale = Chart.getChart(canvas);
        if (stale) stale.destroy();
        const datasets = p.series.map((s) => ({
          label: s.label,
          data: this.vizRows.map((r, i) => ({
            x: offsets[i],
            y:
              r[s.key] === null || r[s.key] === undefined
                ? null
                : Number(r[s.key]),
          })),
          borderColor: s.color,
          backgroundColor: s.color + "22",
          borderWidth: 1.2,
          pointRadius: 0,
          tension: 0.15,
          spanGaps: true,
        }));
        this.charts[p.key] = new Chart(canvas, {
          type: "line",
          data: { datasets },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: false,
            parsing: false,
            plugins: {
              legend: {
                display: p.series.length > 1,
                position: "bottom",
                labels: {
                  boxWidth: 10,
                  boxHeight: 10,
                  padding: 12,
                  font: { size: 11 },
                  color: "#5b6577",
                  usePointStyle: true,
                  pointStyle: "circle",
                },
              },
              tooltip: {
                enabled: this.vizFocusKey === p.key,
                mode: "index",
                intersect: false,
                backgroundColor: "#1f2540",
                titleColor: "#ffffff",
                bodyColor: "#ffffff",
                padding: 10,
                cornerRadius: 8,
                callbacks: {
                  title: (items) =>
                    items.length ? fmtElapsed(items[0].parsed.x) : "",
                },
              },
            },
            scales: {
              x: {
                type: "linear",
                min: this.xStart,
                max: this.xEnd,
                bounds: "data",
                grid: { color: "#eef0f4" },
                ticks: {
                  maxTicksLimit: 6,
                  autoSkip: true,
                  maxRotation: 0,
                  color: "#98a0ad",
                  font: { size: 10 },
                  callback: (v) => fmtElapsed(v),
                },
              },
              y: {
                beginAtZero: false,
                grid: { color: "#eef0f4" },
                ticks: {
                  color: "#98a0ad",
                  font: { size: 10 },
                },
              },
            },
          },
        });
      }
      this.panelEmpty = panelEmpty;
    },
    toggleFocus(key) {
      this.vizFocusKey = this.vizFocusKey === key ? null : key;
      this.$nextTick(() => {
        for (const k of Object.keys(this.charts)) {
          const ch = this.charts[k];
          if (!ch) continue;
          ch.options.plugins.tooltip.enabled = this.vizFocusKey === k;
          ch.update("none");
          ch.resize();
        }
      });
    },
    disposeCharts() {
      for (const k of Object.keys(this.charts)) {
        const ch = this.charts[k];
        if (ch && typeof ch.destroy === "function") ch.destroy();
      }
      this.charts = {};
    },
    closeViz() {
      this.vizOpen = false;
      this.disposeCharts();
      this.resetVizState();
    },
    resetVizState() {
      this.vizTest = null;
      this.vizRows = [];
      this.vizFocusKey = null;
      this.panelEmpty = {};
      this.vizShown = false;
    },
    onVizClosed() {
      // `@hidden` fires after the close animation. If the user reopened the
      // modal mid-transition, vizOpen is already true and this stale event
      // would otherwise clobber the new session's state.
      if (this.vizOpen) return;
      this.disposeCharts();
      this.resetVizState();
    },
    csvEscape(v) {
      if (v === null || v === undefined) return "";
      const s = String(v);
      if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    },
    exportFilename(test) {
      const mac = (this.selectedDevice || "").replace(/:/g, "");
      const t = parseTime(test.test_started_at);
      if (t === null) return `isaac_test_${mac}_unknown.csv`;
      const d = new Date(t);
      const ts =
        `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}` +
        `_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}` +
        `_${pad(d.getMilliseconds(), 3)}`;
      return `isaac_test_${mac}_${ts}.csv`;
    },
    exportKey(test) {
      return String(test.test_started_at || "");
    },
    openRangeExport() {
      const { min, max } = this.rangeBounds;
      this.rangeFrom = min !== null ? toLocalDateInputValue(min) : "";
      this.rangeTo = max !== null ? toLocalDateInputValue(max) : "";
      this.rangeError = "";
      this.rangeExporting = false;
      this.rangeOpen = true;
    },
    onRangeHidden() {
      // Modal closed (X / backdrop / programmatic). Reset transient state but
      // leave rangeFrom/rangeTo so reopening preserves the user's last picks.
      this.rangeExporting = false;
      this.rangeError = "";
    },
    rangeCsvFilename() {
      const mac = (this.selectedDevice || "").replace(/:/g, "");
      const stamp = (s) => (s || "").replace(/-/g, "");
      return `isaac_range_${mac}_${stamp(this.rangeFrom)}_${stamp(this.rangeTo)}.csv`;
    },
    async performRangeExport() {
      if (!this.rangeValid) {
        this.rangeError = "Please pick a valid From/To.";
        return;
      }
      this.rangeError = "";
      this.rangeExporting = true;
      try {
        // The user picks whole days. Expand `to` to end-of-day so the
        // selected last day is fully included; `from` already implies
        // 00:00:00 of that day.
        const res = await customAxios.get("/measurements", {
          params: {
            device_id: this.selectedDevice,
            from: `${this.rangeFrom} 00:00:00`,
            to: `${this.rangeTo} 23:59:59.999`,
          },
        });
        const rows = Array.isArray(res.data && res.data.data)
          ? res.data.data
          : [];
        if (rows.length === 0) {
          this.rangeError = "No rows in this range.";
          this.rangeExporting = false;
          return;
        }
        const header = [
          "Timestamp",
          "Test Started At",
          "Temperature",
          "Humidity",
          "Sensor 1",
          "Sensor 2",
          "Sensor 3",
          "Sensor 4",
          "Heater Voltage",
          "Intervals (s)",
        ];
        const lines = [header.join(",")];
        for (const r of rows) {
          const cells = [
            fmtMs(r.measured_at),
            fmtMs(r.test_started_at),
            r.temperature,
            r.humidity,
            r.sensor1,
            r.sensor2,
            r.sensor3,
            r.sensor4,
            r.heater_voltage,
            r.config,
          ].map(this.csvEscape);
          lines.push(cells.join(","));
        }
        const csv = "﻿" + lines.join("\r\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = this.rangeCsvFilename();
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        this.rangeOpen = false;
      } catch (err) {
        console.error("Range export failed", err);
        const data = err.response && err.response.data;
        this.rangeError =
          (data && data.errors && Object.values(data.errors).flat()[0]) ||
          err.message ||
          "Export failed";
      } finally {
        this.rangeExporting = false;
      }
    },
    async exportTest(test) {
      const key = this.exportKey(test);
      if (this.exportingKey) return;
      this.exportingKey = key;
      try {
        // Reuse rows already loaded for the visualize modal when it's the
        // same test — avoids a redundant round-trip.
        let rows;
        if (
          this.vizTest &&
          this.exportKey(this.vizTest) === key &&
          this.vizRows.length &&
          !this.vizLoading
        ) {
          rows = this.vizRows;
        } else {
          const res = await customAxios.get("/measurements", {
            params: {
              device_id: this.selectedDevice,
              test_started_at: test.test_started_at,
            },
          });
          rows = Array.isArray(res.data && res.data.data) ? res.data.data : [];
        }
        const header = [
          "Timestamp",
          "Temperature",
          "Humidity",
          "Sensor 1",
          "Sensor 2",
          "Sensor 3",
          "Sensor 4",
          "Heater Voltage",
          "Intervals (s)",
        ];
        const lines = [header.join(",")];
        for (const r of rows) {
          const cells = [
            fmtMs(r.measured_at),
            r.temperature,
            r.humidity,
            r.sensor1,
            r.sensor2,
            r.sensor3,
            r.sensor4,
            r.heater_voltage,
            r.config,
          ].map(this.csvEscape);
          lines.push(cells.join(","));
        }
        // RFC 4180 CRLF + UTF-8 BOM so Excel reads non-ASCII correctly.
        const csv = "﻿" + lines.join("\r\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = this.exportFilename(test);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error("Export failed", err);
        this.$bvToast.toast("Failed to export test.", {
          title: "Export",
          variant: "danger",
          autoHideDelay: 3000,
        });
      } finally {
        this.exportingKey = "";
      }
    },
  },
};
</script>

<style scoped>
.measurements-wrap {
  padding-right: 1.5rem;
}
.page-title {
  margin: 0 0 1rem 0;
  font-weight: 700;
}

/* ---------- toolbar ---------- */
.toolbar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  background: white;
  border: 1px solid #e6e8ee;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
}
.toolbar-spacer {
  flex: 1;
}
.device-picker {
  position: relative;
  width: 320px;
  max-width: 100%;
}
.ddl-trigger {
  display: flex;
  align-items: center;
  width: 100%;
  height: 38px;
  padding: 0 0.6rem 0 0.85rem;
  background: #f7f8fb;
  border: 1px solid #e6e8ee;
  border-radius: 8px;
  font-size: 0.9rem;
  color: #1f2540;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
}
.ddl-trigger:hover {
  background: #ffffff;
  border-color: #d8dde9;
}
.ddl-trigger:focus,
.ddl-trigger.ddl-open {
  outline: none;
  background: #ffffff;
  border-color: #2a345d;
  box-shadow: 0 0 0 3px rgba(42, 52, 93, 0.12);
}
.ddl-trigger:not(.has-value) .ddl-value {
  color: #98a0ad;
}
.ddl-value {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ddl-chevron {
  color: #98a0ad;
  font-size: 0.8rem;
  margin-left: 0.5rem;
  transition: transform 0.15s;
}
.ddl-trigger.ddl-open .ddl-chevron {
  transform: rotate(180deg);
}
.ddl-clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin-right: 0.25rem;
  border-radius: 50%;
  color: #98a0ad;
  cursor: pointer;
}
.ddl-clear:hover {
  background: #eef0f4;
  color: #2a345d;
}
.ddl-panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 30;
  background: white;
  border: 1px solid #e6e8ee;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(16, 24, 40, 0.12);
  overflow: hidden;
}
.ddl-search {
  position: relative;
  padding: 0.5rem;
  border-bottom: 1px solid #eef0f4;
}
.ddl-search-icon {
  position: absolute;
  top: 50%;
  left: 1.05rem;
  transform: translateY(-50%);
  color: #98a0ad;
  font-size: 0.85rem;
  pointer-events: none;
}
.ddl-search-input {
  width: 100%;
  height: 32px;
  padding: 0 0.6rem 0 2rem;
  background: #f7f8fb;
  border: 1px solid transparent;
  border-radius: 6px;
  font-size: 0.85rem;
  font-variant-numeric: tabular-nums;
}
.ddl-search-input:focus {
  outline: none;
  background: #ffffff;
  border-color: #2a345d;
  box-shadow: 0 0 0 3px rgba(42, 52, 93, 0.1);
}
.ddl-list {
  max-height: 260px;
  overflow-y: auto;
}
.ddl-item {
  padding: 0.5rem 0.85rem;
  font-size: 0.88rem;
  color: #1f2540;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
}
.ddl-item:hover {
  background: #f7f8fb;
}
.ddl-item.selected {
  background: #eef1f7;
  color: #2a345d;
  font-weight: 600;
}
.ddl-empty {
  padding: 1rem 0.85rem;
  text-align: center;
  font-size: 0.85rem;
  color: #98a0ad;
}
.summary-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.7rem;
  background: #eef1f7;
  color: #2a345d;
  border-radius: 999px;
  font-size: 0.85rem;
  font-variant-numeric: tabular-nums;
}
.summary-label {
  color: #5b6577;
  font-weight: 500;
}
.summary-dot {
  color: #b6bdcb;
}
.export-btn.btn {
  height: 38px;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0 1rem;
  font-size: 0.9rem;
  font-weight: 500;
  color: #ffffff;
  background-color: #2a345d;
  border: 1px solid #2a345d;
  border-radius: 8px;
  transition: background-color 0.15s, border-color 0.15s, box-shadow 0.15s;
}
.export-btn.btn:hover,
.export-btn.btn:focus,
.export-btn.btn:active {
  background-color: #1f2747;
  border-color: #1f2747;
  color: #ffffff;
  box-shadow: none;
}
.export-btn.btn:focus-visible {
  box-shadow: 0 0 0 3px rgba(42, 52, 93, 0.25);
}

/* ---------- loading + empty ---------- */
.loading-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3rem 0;
  color: #5b6577;
}
.empty-state {
  text-align: center;
  padding: 3.5rem 1rem;
  background: white;
  border: 1px dashed #e0e3eb;
  border-radius: 12px;
}
.empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #eef1f7;
  color: #2a345d;
  font-size: 1.6rem;
  margin-bottom: 1rem;
}
.empty-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: #2a345d;
  margin-bottom: 0.35rem;
}
.empty-sub {
  font-size: 0.9rem;
  color: #6b7383;
  max-width: 26rem;
  margin: 0 auto;
}

/* ---------- tests table ---------- */
.tests-card {
  background: white;
  border: 1px solid #e6e8ee;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
}
.tests-table {
  margin: 0;
  font-size: 0.9rem;
}
.tests-table ::v-deep table {
  table-layout: fixed;
  width: 100%;
}
.tests-table ::v-deep thead th {
  background: #f7f8fb;
  color: #5b6577;
  font-weight: 600;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-top: none;
  border-bottom: 1px solid #eaecf2;
  padding: 0.65rem 0.85rem;
  text-align: center;
}
.tests-table ::v-deep tbody td {
  padding: 0.7rem 0.85rem;
  vertical-align: middle;
  border-top: 1px solid #f1f2f6;
  color: #1f2540;
  font-variant-numeric: tabular-nums;
  text-align: center;
}
.tests-table ::v-deep tbody tr:first-child td {
  border-top: none;
}
.tests-table ::v-deep tbody tr:hover td {
  background: #fafbfd;
}
/* Three data columns share remaining width equally; actions stays compact. */
.tests-table ::v-deep thead th:nth-child(1),
.tests-table ::v-deep tbody td:nth-child(1),
.tests-table ::v-deep thead th:nth-child(2),
.tests-table ::v-deep tbody td:nth-child(2),
.tests-table ::v-deep thead th:nth-child(3),
.tests-table ::v-deep tbody td:nth-child(3) {
  width: calc((100% - 7.5rem) / 3);
}
.tests-table ::v-deep thead th:nth-child(4),
.tests-table ::v-deep tbody td:nth-child(4) {
  width: 7.5rem;
}
.tests-table ::v-deep .config-col {
  color: #5b6577;
}
.rows-chip {
  display: inline-block;
  padding: 0.15rem 0.55rem;
  background: #eef1f7;
  color: #2a345d;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.action-group {
  display: inline-flex;
  gap: 0.25rem;
  justify-content: center;
}

.tests-pagination {
  display: flex;
  justify-content: center;
  padding: 0.75rem 0.5rem;
  border-top: 1px solid #eaecf2;
  background: #f7f8fb;
}
.tests-pagination ::v-deep .pagination {
  margin: 0;
}
.tests-pagination ::v-deep .page-link {
  color: #5b6577;
  border: 1px solid #e6e8ee;
  border-radius: 8px !important;
  margin: 0 2px;
  min-width: 32px;
  text-align: center;
}
.tests-pagination ::v-deep .page-item.active .page-link {
  background: #2a345d;
  border-color: #2a345d;
  color: #ffffff;
}
.tests-pagination ::v-deep .page-link:hover {
  background: #eef1f7;
  color: #2a345d;
}
.tests-pagination ::v-deep .page-item.disabled .page-link {
  opacity: 0.5;
  background: transparent;
}
.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid transparent;
  background: transparent;
  border-radius: 8px;
  color: #5b6577;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}
.action-btn:hover:not(:disabled) {
  background: #eef1f7;
  color: #2a345d;
  border-color: #d8dde9;
}
.action-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(42, 52, 93, 0.18);
}
.action-btn--busy,
.action-btn:disabled {
  opacity: 0.6;
  cursor: default;
}
/* ---------- export modal ---------- */
.rng-lead {
  color: #5b6577;
  font-size: 0.9rem;
  margin-bottom: 1rem;
}
.rng-lead strong {
  color: #1f2540;
  font-variant-numeric: tabular-nums;
}
.rng-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 0.75rem;
}
@media (max-width: 600px) {
  .rng-fields {
    grid-template-columns: 1fr;
  }
}
.rng-field {
  display: flex;
  flex-direction: column;
}
.rng-label {
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #5b6577;
  margin-bottom: 0.35rem;
}
.rng-hint {
  color: #5b6577;
  font-size: 0.85rem;
  margin-bottom: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}
.rng-err {
  color: #b02a37;
  font-size: 0.85rem;
  margin-bottom: 0.5rem;
}
.rng-btn-cancel,
.rng-btn-ok {
  height: 38px;
  padding: 0 1.1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  transition: background 0.15s, color 0.15s, border-color 0.15s, box-shadow 0.15s;
}
.rng-btn-cancel {
  background: transparent;
  border: 1px solid #e6e8ee;
  color: #5b6577;
}
.rng-btn-cancel:hover:not(:disabled) {
  background: #f7f8fb;
  border-color: #d8dde9;
  color: #2a345d;
}
.rng-btn-ok {
  background: #2a345d;
  border: 1px solid #2a345d;
  color: #ffffff;
}
.rng-btn-ok:hover:not(:disabled) {
  background: #1f2747;
  border-color: #1f2747;
}
.rng-btn-ok:focus-visible,
.rng-btn-cancel:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(42, 52, 93, 0.25);
}
.rng-btn-ok:disabled,
.rng-btn-cancel:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
/* ---------- viz modal body ---------- */
.viz-loading,
.viz-error {
  text-align: center;
  padding: 2.5rem 0;
  color: #5b6577;
  font-size: 0.9rem;
}
.viz-error {
  color: #b02a37;
}
.viz-error i {
  margin-right: 0.4rem;
}
.viz-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}
.viz-meta-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.viz-meta-hint {
  color: #98a0ad;
  font-size: 0.78rem;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}
.viz-back-btn {
  height: 32px;
  padding: 0 0.85rem;
  font-size: 0.85rem;
}

/* ---------- range slider ---------- */
.range-slider {
  margin: 0 0 1.25rem;
  padding: 0.25rem 0.25rem 0;
}
.range-row {
  position: relative;
  height: 28px;
}
.range-track {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 4px;
  transform: translateY(-50%);
  background: #e6e8ee;
  border-radius: 999px;
}
.range-track-fill {
  position: absolute;
  top: 0;
  bottom: 0;
  background: linear-gradient(90deg, #2a345d, #4a6491);
  border-radius: 999px;
  pointer-events: none;
}
.range-track input[type="range"] {
  position: absolute;
  left: 0;
  right: 0;
  top: -12px;
  width: 100%;
  height: 28px;
  background: transparent;
  -webkit-appearance: none;
  appearance: none;
  pointer-events: none;
}
.range-track input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  pointer-events: auto;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #ffffff;
  border: 2px solid #2a345d;
  box-shadow: 0 1px 4px rgba(16, 24, 40, 0.18);
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;
}
.range-track input[type="range"]::-webkit-slider-thumb:hover {
  transform: scale(1.15);
  box-shadow: 0 2px 6px rgba(16, 24, 40, 0.24);
}
.range-track input[type="range"]::-moz-range-thumb {
  pointer-events: auto;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #ffffff;
  border: 2px solid #2a345d;
  box-shadow: 0 1px 4px rgba(16, 24, 40, 0.18);
  cursor: pointer;
}
.range-labels {
  display: flex;
  justify-content: space-between;
  margin-top: 0.5rem;
  color: #5b6577;
  font-size: 0.78rem;
  font-variant-numeric: tabular-nums;
}

/* ---------- chart grid ---------- */
.viz-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}
.viz-grid.has-focus {
  grid-template-columns: 1fr;
}
.viz-grid.has-focus .viz-panel:not(.viz-panel--focused) {
  display: none;
}
.viz-panel {
  background: #ffffff;
  border: 1px solid #e6e8ee;
  border-radius: 10px;
  padding: 0.75rem 0.9rem 0.9rem;
  cursor: zoom-in;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.viz-panel:hover {
  border-color: #cdd3e0;
  box-shadow: 0 4px 12px rgba(16, 24, 40, 0.06);
}
.viz-panel--focused {
  cursor: default;
  border-color: #2a345d;
  box-shadow: 0 4px 16px rgba(42, 52, 93, 0.1);
}
.viz-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}
.viz-panel-title {
  margin: 0;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #1f2540;
  text-transform: uppercase;
}
.viz-panel-icon {
  color: #98a0ad;
  font-size: 0.85rem;
}
.viz-panel:hover .viz-panel-icon {
  color: #2a345d;
}
.viz-panel canvas {
  height: 200px !important;
  width: 100% !important;
}
.viz-panel--focused canvas {
  height: 420px !important;
}
.viz-panel-empty {
  text-align: center;
  color: #98a0ad;
  font-size: 0.82rem;
  padding: 4rem 0;
}
</style>

<style>
/* Viz modal — match the toolbar/table palette */
.viz-modal .modal-content {
  border: 1px solid #e6e8ee;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(16, 24, 40, 0.18);
  overflow: hidden;
}
.viz-modal-header {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  background: #f7f8fb;
  border-bottom: 1px solid #eaecf2 !important;
  padding: 0.85rem 1.1rem !important;
}
.viz-modal-body {
  padding: 1.25rem !important;
  background: #f4f6fa;
}
.viz-title-block {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}
.viz-title {
  margin: 0;
  font-weight: 600;
  color: #1f2540;
  font-size: 1rem;
}
.viz-subtitle {
  font-size: 0.78rem;
  color: #98a0ad;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
}
.viz-close-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: #5b6577;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.viz-close-btn:hover {
  background: #eef0f4;
  color: #2a345d;
}
.viz-close-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(42, 52, 93, 0.18);
}

/* Range-export modal — match the toolbar/table palette */
.rng-modal .modal-content {
  border: 1px solid #e6e8ee;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(16, 24, 40, 0.18);
  overflow: hidden;
}
.rng-modal-header {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  background: #f7f8fb;
  border-bottom: 1px solid #eaecf2 !important;
  padding: 0.85rem 1.1rem !important;
}
.rng-title {
  margin: 0;
  font-weight: 600;
  color: #1f2540;
  font-size: 1rem;
}
.rng-close-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: #5b6577;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.rng-close-btn:hover {
  background: #eef0f4;
  color: #2a345d;
}
.rng-close-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(42, 52, 93, 0.18);
}

/* Two calendars side by side need extra horizontal room */
.rng-modal .modal-dialog {
  max-width: 760px;
}
@media (max-width: 600px) {
  .rng-modal .modal-dialog {
    max-width: 95vw;
  }
}

/* Inline b-calendar */
.rng-modal .rng-calendar {
  width: 100%;
  background: #ffffff;
  border: 1px solid #e6e8ee;
  border-radius: 10px;
  padding: 0.5rem 0.75rem;
}
.rng-modal .rng-calendar:focus-within {
  border-color: #2a345d;
  box-shadow: 0 0 0 3px rgba(42, 52, 93, 0.12);
}
.rng-modal .rng-calendar .b-calendar-nav {
  margin-bottom: 0.25rem;
  gap: 0.25rem;
}
.rng-modal .rng-calendar .b-calendar-nav .btn {
  width: 32px;
  height: 32px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  color: #5b6577;
  background: transparent;
  border: none;
}
.rng-modal .rng-calendar .b-calendar-nav .btn:hover {
  background: #eef1f7;
  color: #2a345d;
}
.rng-modal .rng-calendar header output {
  font-weight: 600;
  color: #1f2540;
  padding: 0.25rem 0;
  border: none !important;
  background: transparent !important;
  font-size: 0.95rem;
}
.rng-modal .rng-calendar [role="columnheader"] {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #98a0ad;
  padding: 0.35rem 0;
}
.rng-modal .rng-calendar [role="gridcell"] .btn {
  width: 100%;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  border: 1px solid transparent;
  background: transparent;
  color: #1f2540;
  font-size: 0.85rem;
  font-variant-numeric: tabular-nums;
}
.rng-modal .rng-calendar [role="gridcell"] .btn:hover {
  background: #eef1f7 !important;
  color: #2a345d !important;
}
.rng-modal .rng-calendar [role="gridcell"] .btn.btn-primary,
.rng-modal .rng-calendar [role="gridcell"] .btn.active {
  background: #2a345d !important;
  border-color: #2a345d !important;
  color: #ffffff !important;
}
.rng-modal .rng-calendar [role="gridcell"] .btn.text-secondary:not(.btn-primary) {
  font-weight: 700;
  color: #2a345d !important;
}
.rng-modal .rng-calendar [role="gridcell"] .btn.text-muted,
.rng-modal .rng-calendar [role="gridcell"] .btn.disabled {
  color: #c8cdd8 !important;
}
.rng-modal .rng-calendar .form-control:focus {
  border-color: #2a345d;
  box-shadow: 0 0 0 3px rgba(42, 52, 93, 0.12);
}
.rng-modal-body {
  padding: 1.25rem !important;
}
.rng-modal-footer {
  border-top: 1px solid #eaecf2 !important;
  padding: 0.75rem 1.1rem !important;
  background: #ffffff;
}
</style>
